/**
 * Surface flags — what the product *offers* a cabinet, not what a user is
 * permitted to reach.
 *
 * This is deliberately not access control. The person/CRM API endpoints stay
 * open because the dossier flows depend on them; hiding a surface only removes
 * it from the product's own navigation. Anything that must actually be denied
 * belongs in `require_permission` on the backend, never here.
 *
 * One hook, because a feature reachable from seven places is hidden correctly
 * only when all seven agree. CRM alone appears in the sidebar, the mobile
 * drawer, the bottom bar, the command palette, a keyboard shortcut, a dashboard
 * tile and a back-link — and a flag applied to six of them is not a flag, it is
 * a bug with extra steps.
 */

import { useAuth } from "@/lib/auth-context";

export interface Features {
  /**
   * CRM: the light relationship layer beside the client files that carry the
   * regulatory work. Off by default: the model default is false and the
   * 2026-08-27 migration (`20260827000001`) switched it off for every
   * cabinet. A cabinet turns it on from Réglages → Organisation. Person pages
   * stay reachable without it (#563), but the `/crm` list does not.
   */
  crm: boolean;
}

export function useFeatures(): Features {
  const { organization } = useAuth();
  // Default closed while the organization is still loading: revealing a
  // surface and then pulling it away is worse than showing it a beat late.
  return { crm: organization?.crm_enabled ?? false };
}
